import _ from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {collectEnumValues} from '../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {StyledRequiredSpan} from '../../../../../libs/shared/ui/src/components/fields/fieldLabel';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {OnboardingGuideSlide, OnboardingGuideSlideProps} from '../../guide/onboardingGuideSlide';
import {OnboardingSelectionButton} from '../onboardingSelectionButton';
import {
  StyledColumnFormDescriptionDiv,
  StyledOnboardingFormTitleDiv,
  StyledOnboardingFormTitlesDiv,
} from '../onboardingWelcomeHelpers';

/*
 * Props.
 */

export interface OnboardingWorkflowsTagRuleSlideProps
  extends Pick<OnboardingGuideSlideProps, 'navPrimary' | 'navSecondary'> {
  tagName: OnboardingWorkflowsTagRuleTagName | undefined;
  onChangeTagName: (arg: OnboardingWorkflowsTagRuleTagName | undefined) => void;
  customTagName: string;
  onChangeCustomTagName: (arg: string) => void;
  suggestedKeywords: ReadonlyArray<string>;
  onChangeSuggestedKeywords: (arg: ReadonlyArray<string>) => void;
  customKeywords: string;
  onChangeCustomKeywords: (arg: string) => void;
}

enum SuggestedTagName {
  BILLING = 'billing',
  URGENT = 'urgent',
  CUSTOMER_FEEDBACK = 'customer_feedback',
}

export enum OnboardingWorkflowsTagRuleCustomTagName {
  ENTER_SOMETHING_ELSE = 'enter_something_else',
}

export type OnboardingWorkflowsTagRuleTagName = SuggestedTagName | OnboardingWorkflowsTagRuleCustomTagName;

interface TagNameConstantsData {
  label: ReactNode;
  displayName?: string;
}

export const OnboardingWorkflowsTagRuleTagNamesConstants: Record<
  OnboardingWorkflowsTagRuleTagName,
  TagNameConstantsData
> = {
  [SuggestedTagName.BILLING]: {
    label: <Trans id="onboardingwelcome-workflows-tag-rule-billing-label">Billing</Trans>,
    displayName: 'Billing',
  },
  [SuggestedTagName.URGENT]: {
    label: <Trans id="onboardingwelcome-workflows-tag-rule-urgent-label">Urgent</Trans>,
    displayName: 'Urgent',
  },
  [SuggestedTagName.CUSTOMER_FEEDBACK]: {
    label: <Trans id="onboardingwelcome-workflows-tag-rule-customer-feedback-label">Customer feedback</Trans>,
    displayName: 'Customer feedback',
  },
  [OnboardingWorkflowsTagRuleCustomTagName.ENTER_SOMETHING_ELSE]: {
    label: (
      <Trans id="onboardingwelcome-workflows-tag-rule-enter-something-else-label">Enter something else</Trans>
    ),
  },
};

const SuggestedKeywords: Record<OnboardingWorkflowsTagRuleTagName, ReadonlyArray<string>> = {
  [SuggestedTagName.BILLING]: ['invoice', 'receipt'],
  [SuggestedTagName.URGENT]: ['important', 'ASAP'],
  [SuggestedTagName.CUSTOMER_FEEDBACK]: ['feedback', 'request'],
  [OnboardingWorkflowsTagRuleCustomTagName.ENTER_SOMETHING_ELSE]: [],
};

/*
 * Styles.
 */

const StyledHalfContainer = styled.div`
  flex: 0 0 50%;
`;

const StyledSelectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const StyledTagNameSelectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledSuggestedTagNameSelectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  > * {
    width: fit-content;
  }
`;

const StyledCustomTagNameSelectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  > * {
    width: fit-content;
  }
`;

const StyledContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledCtaWrapper = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  line-height: 12px;
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledKeywordsSelectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledKeywordsCtaWrapper = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledSuggestedKeywordsSelectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-bottom: 4px;
  > * {
    width: fit-content;
  }
`;

const StyledCustomKeywordsSelectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  > * {
    width: fit-content;
  }
`;

const StyledKeywordsDetailsWrapper = styled.div`
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const OnboardingWorkflowsTagRuleSlide: FC<OnboardingWorkflowsTagRuleSlideProps> = (props) => {
  const {
    tagName,
    onChangeTagName,
    customTagName,
    onChangeCustomTagName,
    suggestedKeywords,
    onChangeSuggestedKeywords,
    customKeywords,
    onChangeCustomKeywords,
    ...slideProps
  } = props;

  const resetKeywords = () => {
    onChangeSuggestedKeywords([]);
    onChangeCustomKeywords('');
  };

  return (
    <OnboardingGuideSlide {...slideProps} isLeftContent>
      <StyledHalfContainer>
        <StyledOnboardingFormTitlesDiv>
          <StyledOnboardingFormTitleDiv>
            <Trans id="onboardingwelcome-workflows-tag-rule-title">
              Create a rule to track conversation trends
            </Trans>
          </StyledOnboardingFormTitleDiv>
        </StyledOnboardingFormTitlesDiv>
        <StyledColumnFormDescriptionDiv>
          <Trans id="onboardingwelcome-workflows-tag-rule-description">
            Tags enable you to track trends in analytics or set up rules based on them. We&rsquo;ll help you
            create your first tag based on keywords, and you can set up more tags in Settings &gt; Rules
            later.
          </Trans>
        </StyledColumnFormDescriptionDiv>
        <StyledContentWrapper>
          <StyledCtaWrapper>
            <Trans id="onboardingwelcome-workflows-tag-rule-cta">
              Choose a tag you&rsquo;d like to use in this inbox, or create your own
              <StyledRequiredSpan>*</StyledRequiredSpan>
            </Trans>
          </StyledCtaWrapper>
          <StyledSelectionWrapper>
            <StyledTagNameSelectionWrapper>
              <StyledSuggestedTagNameSelectionWrapper>
                {collectEnumValues(SuggestedTagName).map((tagNameOption) => (
                  <OnboardingSelectionButton
                    key={tagNameOption}
                    type="radio"
                    isChecked={tagName === tagNameOption}
                    onClick={() => {
                      if (tagName !== tagNameOption) {
                        onChangeCustomTagName('');
                        onChangeTagName(tagNameOption);
                        onChangeSuggestedKeywords(SuggestedKeywords[tagNameOption]);
                        onChangeCustomKeywords('');
                      }
                    }}
                    label={OnboardingWorkflowsTagRuleTagNamesConstants[tagNameOption].label}
                  />
                ))}
              </StyledSuggestedTagNameSelectionWrapper>
              <StyledCustomTagNameSelectionWrapper>
                <OnboardingSelectionButton
                  key={OnboardingWorkflowsTagRuleCustomTagName.ENTER_SOMETHING_ELSE}
                  type="radio"
                  isChecked={tagName === OnboardingWorkflowsTagRuleCustomTagName.ENTER_SOMETHING_ELSE}
                  value={customTagName}
                  onChangeValue={(customTagNameValue) => {
                    onChangeCustomTagName(customTagNameValue);

                    if (customTagNameValue) {
                      onChangeTagName(OnboardingWorkflowsTagRuleCustomTagName.ENTER_SOMETHING_ELSE);
                    }
                  }}
                  onClick={() => {
                    if (!customTagName) {
                      onChangeTagName(undefined);
                      resetKeywords();
                    }
                  }}
                  label={
                    OnboardingWorkflowsTagRuleTagNamesConstants[
                      OnboardingWorkflowsTagRuleCustomTagName.ENTER_SOMETHING_ELSE
                    ].label
                  }
                  isFreeform
                />
              </StyledCustomTagNameSelectionWrapper>
            </StyledTagNameSelectionWrapper>
            {tagName && (
              <StyledKeywordsSelectionWrapper>
                <StyledKeywordsCtaWrapper>
                  <Trans id="onboardingwelcome-workflows-tag-rule-keywords-cta">
                    Define a few keywords (in subject or body) to trigger this tag
                    <StyledRequiredSpan>*</StyledRequiredSpan>
                  </Trans>
                </StyledKeywordsCtaWrapper>
                {(SuggestedKeywords[tagName]?.length ?? 0) > 0 && (
                  <StyledSuggestedKeywordsSelectionWrapper>
                    {Object.values(SuggestedKeywords[tagName]).map((suggestedKeywordsOption) => (
                      <OnboardingSelectionButton
                        key={suggestedKeywordsOption}
                        type="checkbox"
                        isChecked={suggestedKeywords.includes(suggestedKeywordsOption)}
                        onClick={() => {
                          if (suggestedKeywords.includes(suggestedKeywordsOption)) {
                            return onChangeSuggestedKeywords(
                              suggestedKeywords.filter(
                                (suggestedKeyword) => suggestedKeyword !== suggestedKeywordsOption,
                              ),
                            );
                          }

                          return onChangeSuggestedKeywords([
                            ...new Set([...suggestedKeywords, suggestedKeywordsOption]),
                          ]);
                        }}
                        label={<span>&ldquo;{suggestedKeywordsOption}&rdquo;</span>}
                      />
                    ))}
                  </StyledSuggestedKeywordsSelectionWrapper>
                )}
                <StyledCustomKeywordsSelectionWrapper>
                  <OnboardingSelectionButton
                    key="custom-keywords"
                    type="checkbox"
                    isChecked={Boolean(parseCustomKeywords(customKeywords).length)}
                    value={customKeywords}
                    onChangeValue={onChangeCustomKeywords}
                    onClick={_.noop}
                    placeholder="Enter keywords"
                    onClickCheckMark={() => {
                      if (parseCustomKeywords(customKeywords).length) {
                        onChangeCustomKeywords('');
                      }
                    }}
                    label={
                      <Trans id="onboardingwelcome-workflows-tag-rule-custom-keywords-placeholder">
                        Enter keywords
                      </Trans>
                    }
                    isStandalone={tagName === OnboardingWorkflowsTagRuleCustomTagName.ENTER_SOMETHING_ELSE}
                    isFreeform
                  />
                </StyledCustomKeywordsSelectionWrapper>
                <StyledKeywordsDetailsWrapper>
                  <Trans id="onboardingwelcome-workflows-tag-rule-keywords-details">
                    Separate multiple keywords by &ldquo;;&rdquo;
                  </Trans>
                </StyledKeywordsDetailsWrapper>
              </StyledKeywordsSelectionWrapper>
            )}
          </StyledSelectionWrapper>
        </StyledContentWrapper>
      </StyledHalfContainer>
    </OnboardingGuideSlide>
  );
};

export function parseCustomTagName(customTagName: string) {
  return customTagName.trim();
}

export function parseCustomKeywords(customKeywords: string) {
  return customKeywords
    .split(';')
    .map((customKeyword) => customKeyword.trim())
    .filter(Boolean);
}
